import React, { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Home, Calendar, AlertTriangle, ClipboardList, User, Users, FileText,
  School, Target, CalendarX, Clock, FileSignature, FileCheck, AlertCircle, FileSpreadsheet,
  BarChart3, Database, LogOut, GraduationCap, Menu, X, ChevronLeft, KeyRound,
  Radio, BookOpen, Star, MessageCircle, ClipboardCheck, Landmark,
} from "lucide-react";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import useUnreadMessages from "@/hooks/useUnreadMessages";

const ICONS = {
  Home, Calendar, AlertTriangle, ClipboardList, User, Users, FileText,
  School, Target, CalendarX, Clock, FileSignature, FileCheck, AlertCircle, BarChart3, Database,
  Radio, BookOpen, Star, MessageCircle, FileSpreadsheet, ClipboardCheck, Landmark,
};

const NAV = [
  { path: "general", label: "معلومات عامة", icon: "Home" },
  { path: "semester-plan", label: "الخطة الأسبوعية", icon: "Calendar" },
  { path: "student-records", label: "سجلات الطلاب", icon: "FileSpreadsheet" },
  { path: "student-followup", label: "متابعة الطالب", icon: "ClipboardCheck" },
  { path: "special-cases", label: "الحالات الخاصة", icon: "AlertTriangle" },
  { path: "daily-incidents", label: "المواقف اليومية", icon: "AlertCircle" },
  { path: "individual-sessions", label: "الإرشاد الفردي", icon: "User" },
  { path: "group-sessions", label: "الإرشاد الجمعي", icon: "Users" },
  { path: "info-form", label: "دراسة حالة", icon: "FileText" },
  { path: "academic-record", label: "السجل الأكاديمي", icon: "BookOpen" },
  { path: "gifted-students", label: "سجلات الموهوبين", icon: "Star" },
  { path: "parents", label: "أولياء الأمور", icon: "Users" },
  { path: "class-visits", label: "الزيارات الصفية", icon: "School" },
  { path: "radio-broadcast", label: "الإذاعة الإرشادية", icon: "Radio" },
  { path: "guidance-lessons", label: "الحصص الإرشادية", icon: "BookOpen" },
  { path: "programs", label: "البرامج الإرشادية", icon: "Target" },
  { path: "absence", label: "الغياب والتأخر المتكرر", icon: "CalendarX" },
  { path: "behavior-contract", label: "التعاقد السلوكي", icon: "FileSignature" },
  { path: "monthly-report", label: "التقرير الشهري", icon: "BarChart3" },
  { path: "guidance-committee", label: "لجنة التوجيه والإرشاد", icon: "ClipboardCheck" },
  { path: "student-council", label: "المجلس الطلابي", icon: "Landmark" },
  { path: "backup", label: "النسخ الاحتياطي", icon: "Database" },
  { path: "communication", label: "التواصل والرسائل", icon: "MessageCircle" },
];

export default function CounselorLayout({ children, basePath = "/counselor", backLink = null }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const unreadMessages = useUnreadMessages(user?.id);
  const canViewCaseStudy = user?.role === "counselor"
    || (user?.role === "admin" && user?.can_evaluate_counselors === true);
  const visibleNav = NAV.filter((item) => (item.path !== "info-form" || canViewCaseStudy) && (item.path !== "student-followup" || !backLink));

  const Sidebar = (
    <aside className="no-print w-72 shrink-0 bg-white border-s border-[#e6e2d8] flex flex-col h-screen sticky top-0">
      <div className="p-4 border-b border-[#e6e2d8]">
        <Link to={backLink || basePath} className="block">
          <img
            src="/branding/sidebar-school-marks.png"
            alt="شركة كيان النموذجية"
            data-platform-logo="true"
            data-testid="sidebar-kayan-logo"
            className="w-full h-auto object-contain mb-2"
          />
          <div className="text-center">
            <div className="font-bold text-[#1A201C] text-sm" style={{ fontFamily: "IBM Plex Sans Arabic" }}>منصة الإرشاد الطلابي</div>
            <div className="text-xs text-[#4A554E]">شركة كيان النموذجية</div>
          </div>
        </Link>
      </div>
      {backLink && (
        <Link to={backLink} className="flex items-center gap-2 mx-4 mt-3 px-3 py-2 text-sm text-[#3E6A56] hover:bg-[#3E6A56]/5 rounded-lg">
          <ChevronLeft className="w-4 h-4" /> رجوع للمشرف
        </Link>
      )}
      <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
        {visibleNav.map((item) => {
          const Icon = ICONS[item.icon] || Home;
          return (
            <NavLink
              key={item.path}
              to={`${basePath}/${item.path}`}
              onClick={() => setOpen(false)}
              data-testid={`sidebar-link-${item.path}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
                  isActive
                    ? "bg-[#3E6A56]/10 text-[#3E6A56] font-semibold"
                    : "text-[#4A554E] hover:bg-[#F9F8F6] hover:text-[#1A201C]"
                }`
              }
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span>{item.label}</span>
              {item.path === "communication" && unreadMessages > 0 && (
                <span className="ms-auto inline-flex min-w-5 h-5 items-center justify-center rounded-full bg-[#B84B4B] px-1 text-[10px] font-bold text-white" aria-label={`${unreadMessages} رسائل غير مقروءة`}>
                  {unreadMessages}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-4 border-t border-[#e6e2d8]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-[#3E6A56]/10 text-[#3E6A56] flex items-center justify-center font-bold text-sm">
            {user?.name?.[0] || "?"}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[#1A201C] truncate">{user?.name}</div>
            <div className="text-xs text-[#4A554E]">{user?.role === "admin" ? "مشرف" : "مرشد"}</div>
          </div>
        </div>
        <ChangePasswordDialog
          trigger={
            <button className="w-full flex items-center justify-center gap-2 py-2 mb-2 rounded-xl text-sm text-[#3E6A56] hover:bg-[#3E6A56]/10 transition" data-testid="change-password-trigger">
              <KeyRound className="w-4 h-4" /> تغيير كلمة المرور
            </button>
          }
        />
        <button
          onClick={logout}
          data-testid="logout-button"
          className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-sm text-[#B84B4B] hover:bg-[#B84B4B]/10 transition"
        >
          <LogOut className="w-4 h-4" /> تسجيل الخروج
        </button>
      </div>
    </aside>
  );

  return (
    <div className="platform-shell min-h-screen flex" dir="rtl" data-platform-shell>
      <div className="hidden lg:block">{Sidebar}</div>

      {/* Mobile drawer */}
      {open && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setOpen(false)} />
          <div className="lg:hidden fixed top-0 right-0 z-50">{Sidebar}</div>
        </>
      )}

      <div className="flex-1 min-w-0">
        <header className="no-print lg:hidden sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-[#e6e2d8] p-4 flex items-center justify-between">
          <button onClick={() => setOpen(true)} className="p-2 rounded-lg hover:bg-[#F9F8F6]">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/branding/sidebar-school-marks.png" alt="كيان وشعارات المدارس" data-platform-logo="true" className="h-12 w-auto max-w-[45vw] object-contain" />
            <div className="font-bold text-[#1A201C] text-sm">منصة الإرشاد الطلابي</div>
          </div>
        </header>
        <main className="p-6 sm:p-8 lg:p-10 max-w-6xl">
          {children}
        </main>
      </div>
    </div>
  );
}
